/**
 * The ONLY module that talks to the `stocktakeMessages` router.
 *
 * Keeping the surface area to one file means the panel, the composer and the
 * notify dialog all consume the same normalised hooks, and the thread's
 * polling cadence + invalidation policy live in one place rather than at seven
 * call sites.
 *
 * Types come straight from `@threadheads/shared` — `StocktakeMessageSide` is
 * resolved PER CALLER server-side ('you' / 'them' / 'system'), so the panel
 * never has to work out whose message it is looking at.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import type { StocktakeNotifyConfig, StocktakeNotifyMode } from '@threadheads/shared';

const POLL_MS = 30_000;

const DEFAULT_NOTIFY: StocktakeNotifyConfig = {
  mode: 'none',
  channel: null,
  slackConnected: false,
  channels: [],
};

export function useStocktakeThread(proposalId: string | null, enabled: boolean) {
  const query = trpc.stocktakeMessages.list.useQuery(
    { proposalId: proposalId ?? '' },
    {
      enabled: enabled && !!proposalId,
      refetchInterval: POLL_MS,
      refetchIntervalInBackground: false,
      staleTime: 10_000,
    },
  );

  return {
    messages: query.data?.messages ?? [],
    unreadCount: query.data?.unreadCount ?? 0,
    isLoading: query.isLoading && enabled && !!proposalId,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useStocktakeMessageActions(proposalId: string | null) {
  const { t } = useTranslation('inventory');
  const utils = trpc.useUtils();

  const invalidateThread = useCallback(() => {
    void utils.stocktakeMessages.list.invalidate();
  }, [utils]);

  const post = trpc.stocktakeMessages.post.useMutation({
    onSuccess: invalidateThread,
    onError: (error: { message: string }) => {
      toast({
        title: t('stocktakes.error.sendMessage'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const markRead = trpc.stocktakeMessages.markRead.useMutation({
    onSuccess: () => {
      invalidateThread();
      void utils.notifications.unreadCount.invalidate();
    },
  });

  /** Post to an EXPLICIT thread. The bulk-approve sweep walks several
   *  proposals in one pass, so it must never inherit the open one. */
  const sendMessageTo = useCallback(
    (targetProposalId: string, body: string, skus: string[] = []) =>
      post.mutateAsync({
        proposalId: targetProposalId,
        body,
        ...(skus.length > 0 ? { skus } : {}),
      }),
    [post],
  );

  const sendMessage = useCallback(
    (body: string, skus: string[] = []) =>
      proposalId ? sendMessageTo(proposalId, body, skus) : Promise.resolve(null),
    [sendMessageTo, proposalId],
  );

  return { post, markRead, sendMessage, sendMessageTo, isSending: post.isPending };
}

export function useStocktakeNotifyConfig(proposalId: string | null, enabled: boolean) {
  const { t } = useTranslation('inventory');
  const utils = trpc.useUtils();

  const query = trpc.stocktakeMessages.getNotifyConfig.useQuery(
    { proposalId: proposalId ?? '' },
    { enabled: enabled && !!proposalId, staleTime: 5 * 60_000 },
  );

  const mutation = trpc.stocktakeMessages.setNotifyConfig.useMutation({
    onSuccess: () => {
      void utils.stocktakeMessages.getNotifyConfig.invalidate();
    },
    onError: (error: { message: string }) => {
      toast({
        title: t('stocktakes.error.notifyConfig'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    config: query.data ?? DEFAULT_NOTIFY,
    isLoading: query.isLoading && enabled && !!proposalId,
    save: (mode: StocktakeNotifyMode, channel: string | null) => {
      if (!proposalId) return;
      mutation.mutate({ proposalId, mode, ...(channel ? { channel } : {}) });
    },
    isSaving: mutation.isPending,
  };
}
